// Client → server intents (validated on the server with the same zod schemas) and server → client events.
import { z } from "zod";
import { HANDLE_IDS, NODE_IDS, POWER_SYSTEMS, SWITCH_IDS, type NodeId } from "./ids.js";
import type { AudioHook } from "./audio.js";

export const Intents = {
  "move.to":         z.object({ nodeId: z.enum(NODE_IDS) }).strict(),
  "toggle.flip":     z.object({ switchId: z.enum(SWITCH_IDS) }).strict(),
  "hold.press":      z.object({ task: z.enum(["surge", "coolant"]).default("surge"), handleId: z.enum(HANDLE_IDS) }).strict(),
  "hold.release":    z.object({ task: z.enum(["surge", "coolant"]).default("surge"), handleId: z.enum(HANDLE_IDS) }).strict(),
  /** Power allocation: set one system's units (the server clamps to the reactor output). */
  "power.set":       z.object({ system: z.enum(POWER_SYSTEMS), value: z.number().int().min(0).max(4) }).strict(),
  /** Replace verb: take the spare where it is stored, socket it where it belongs. */
  "replace.take":    z.object({}).strict(),
  /** Test/tuning affordance: fire a director event on demand. Only honoured in seeded rooms. */
  "debug.fire":      z.object({ event: z.enum(["breaker", "coolant", "relay"]) }).strict(),
  "replace.socket":  z.object({}).strict(),
  "mission.restart": z.object({}).strict(),
  /** Sign (or withdraw from) the request to abandon the contract and start over. */
  "mission.vote":    z.object({}).strict(),
  /** WebRTC signaling relay: the server forwards `payload` to `to` untouched and never inspects media. */
  "rtc.signal":      z.object({ to: z.string().min(1).max(32), epoch: z.string().min(1).max(16), payload: z.object({
                        description: z.object({ type: z.enum(["offer","answer","pranswer","rollback"]), sdp: z.string().optional() }).optional(),
                        candidate: z.object({ candidate: z.string(), sdpMid: z.string().nullable().optional(), sdpMLineIndex: z.number().nullable().optional(), usernameFragment: z.string().nullable().optional() }).nullable().optional(),
                      }).strict() }).strict(),
  "intercom.mode":   z.object({ mode: z.enum(["av","audio","none"]) }).strict(),
} as const;

export type IntentName = keyof typeof Intents;
export type IntentPayload<K extends IntentName> = z.infer<(typeof Intents)[K]>;

/** Options sent with create/joinById. `token` is reserved for Phase 2 accounts and ignored in M1. */
export interface JoinOptions {
  name: string;
  password: string;
  /** Stable per-browser guest id (localStorage). Untrusted identity hint until Phase 2 auth replaces it. */
  userId: string;
  token?: string;
  /** Site access code, when the deployment is gated (public phase). Checked server-side on every join. */
  accessCode?: string;
}

/** Options for room creation. Superset of JoinOptions. */
export interface CreateOptions extends JoinOptions {
  /** Optional deterministic seed for the director RNG (tests / repro). */
  seed?: number;
  /** Create the fixed development hull (DEV_HULL_CODE) instead of a random code. */
  devHull?: boolean;
}

/** Server → client, message type "event". Carries the audio hook and its corporate copy. */
export interface ServerEvent {
  hook: AudioHook;
  text: string;
  nodeId?: NodeId;
  tick: number;
}

export const EVENT_MESSAGE = "event" as const;

/** Server → client relay of another crew member's signaling payload. Message type: "rtc.signal". */
export interface RtcSignal { from: string; epoch: string; payload: IntentPayload<"rtc.signal">["payload"] }
export const RTC_MESSAGE = "rtc.signal" as const;
